/* @jsxImportSource react */

import type { ReactNode } from 'react';
import { SWRConfig } from 'swr';

import config from '@packages/config';
import type { Locale } from '@packages/config';
import { ErrorBoundary } from '@packages/shared/src/ui/ErrorBoundary/ErrorBoundary';
import type { Device } from '@packages/shared/src/utils/types';

import { AppBannerSlot } from '../AppBanner/AppBannerSlot.client';
import { ResponsiveNavigationLoaderServer } from '../Navigation/ResponsiveNavigation/ResponsiveNavigationLoader.Server';
import { ResponsiveHeaderLayout } from '../ResponsiveHeaderLayout';
import { LinkedLogo } from '../Toolbar/LinkedLogo';
import { SearchField } from '../Toolbar/SearchField/SearchField';
import { SlotsDefault } from '../MetaNavigation/DesktopSlots/SlotsDefault';
import { BypassHeaderAndSearchLinks } from '../ResponsiveHeaderLayout.BypassBlocks';

type DefaultHeaderProps = {
  locale: Locale;
  device: Device;
  /** Only for compatibility with apps/header, to make it possible to use the navigation in the pages router still */
  navigation?: ReactNode;
};

/**
 * Integrated header module with appbanner (only mobile), toolbar and navigation.
 * This component is able to get its data whether from client or it accepts the SEO relevant initial data from server to populate SWR cache and to make its further fetches and revalidations on client.
 */
export const DefaultHeader = ({ locale, device, navigation }: DefaultHeaderProps) => {
  const {
    appBanner: { enabled: appBannerEnabled },
    isTenantClosing,
  } = config;

  return (
    <>
      <BypassHeaderAndSearchLinks />
      <ErrorBoundary>
        {device === 'mobile' && appBannerEnabled && <AppBannerSlot position="above" />}
      </ErrorBoundary>
      <ResponsiveHeaderLayout
        logo={<LinkedLogo />}
        searchField={
          isTenantClosing ? null : (
            <SWRConfig>
              <SearchField />
            </SWRConfig>
          )
        }
        icons={<SlotsDefault />}
        navigation={
          isTenantClosing
            ? null
            : (navigation ?? <ResponsiveNavigationLoaderServer locale={locale} device={device} />)
        }
      />
      <ErrorBoundary>
        {device === 'mobile' && appBannerEnabled && <AppBannerSlot position="below" />}
      </ErrorBoundary>
    </>
  );
};
